import React, { useEffect, useState } from "react";

import { getLocalStorageCountry, useAddFarm, useGetFarmDetails, useTranslation, useUpdateFarmDetails } from "utils";

import { COUNTRY_CODE } from "consts";

import { AddFarmDetailsRequest, UpdateFarmDetailsRequest } from "models";

import {
  AddressForm,
  Anchor,
  Button,
  Col,
  Drawer,
  Form,
  IllustrationCard,
  Input,
  InputMasked,
  InputNumber,
  LinkMoreInfo,
  PageSpin,
  Row,
  StickyFooterButtons,
  Svg,
  Message as message,
} from "components";

export type FarmDetailsProps = {
  selectedFarmId: string;
  isOnboarding: boolean;
  handleBack: () => void;
  onFinish: () => void;
};

export const FarmDetails = ({ selectedFarmId, handleBack, onFinish }: FarmDetailsProps) => {
  const { mutate: addFarm } = useAddFarm();
  const { mutate: updateFarmDetails } = useUpdateFarmDetails({ farmId: selectedFarmId });
  const { data: farm, isFetching: isFetchingFarmDetails } = useGetFarmDetails({ farmId: selectedFarmId });

  const [form] = Form.useForm<AddFarmDetailsRequest | UpdateFarmDetailsRequest>();

  const defaultCountry = farm?.country ?? getLocalStorageCountry() ?? COUNTRY_CODE.GERMANY;

  const { t } = useTranslation(["onboarding", "enums"]);

  const [saving, setSaving] = useState<boolean>(false);
  const [showPrivacyDrawer, setShowPrivacyDrawer] = useState<boolean>(false);

  const complete = (formData: AddFarmDetailsRequest | UpdateFarmDetailsRequest) => {
    setSaving(true);

    // If user is adding a second or xth farm
    // we want to call a different endpoint to create a new farm.
    // For the first farm this will actually use updateFarmDetails though,
    // because there was already a default farm created.
    if (!selectedFarmId) {
      addFarm(
        { farmDetails: { ...formData, country: defaultCountry } as AddFarmDetailsRequest },
        {
          onSuccess: () => {
            message({ content: t("farmCreated"), icon: "tick" });
            onFinish?.();
          },
          onSettled: () => {
            setSaving(false);
          },
        }
      );
    } else {
      updateFarmDetails(
        { farmDetails: formData as UpdateFarmDetailsRequest },
        {
          onSuccess: () => {
            message({ content: t("farmSaved"), icon: "tick" });
            onFinish?.();
          },
          onSettled: () => {
            setSaving(false);
          },
        }
      );
    }
  };

  const incomplete = () => {
    message({ content: t("errors.mandatoryFields"), icon: "error" });
  };

  const handlePrivacyDrawer = () => {
    setShowPrivacyDrawer(!showPrivacyDrawer);
  };

  useEffect(() => {
    if (!isFetchingFarmDetails && farm && selectedFarmId) {
      form.validateFields();
    }
  }, [form, isFetchingFarmDetails, farm, selectedFarmId]);

  return (
    <>
      {showPrivacyDrawer && (
        <Drawer title={t("privacy.headline")} visible={showPrivacyDrawer} onClose={() => handlePrivacyDrawer()} className="privacy-drawer">
          <IllustrationCard>
            <Svg svgName="misc/data_privacy" />
          </IllustrationCard>
          <h2 className="margin-bottom-200">{t("privacy.title")}</h2>
          <p>{t("privacy.description")}</p>
          <Anchor href="/privacy-policy" target="_blank">
            {t("privacy.link")}
          </Anchor>
        </Drawer>
      )}
      <section className="container tiny">
        {selectedFarmId ? (
          <Row className="margin-top-200">
            <Col>
              <h1>{t("farmUpdateHeadline")}</h1>
            </Col>
          </Row>
        ) : (
          <Row className="margin-top-200">
            <Col>
              <h1>{t("farmDetails.headline")}</h1>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <p style={{ maxWidth: "768px" }}>{t("farmDetails.description")}</p>
            <LinkMoreInfo onClick={() => handlePrivacyDrawer()}>{t("farmDetails.privacy")}</LinkMoreInfo>
          </Col>
        </Row>
        {!isFetchingFarmDetails ? (
          /* @ts-expect-error need to convert to tsx */
          <Form name="farm" onFinish={(formData) => complete(formData)} onFinishFailed={(v) => incomplete(v)} form={form} validateTrigger="onBlur">
            <Form.Item name="id" hidden={true} initialValue={farm ? farm.id : undefined} />
            <Row className="padding-top-350">
              <Col>
                <Input
                  dataTestId="create-farm-name"
                  name="name"
                  label={t("farmDetails.name")}
                  rules={[{ required: true, message: t("farmDetails.errorMessage.farmNameMissing") }]}
                  initialValue={(farm && farm.name) || undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <InputNumber
                  dataTestId="create-farm-size"
                  name="size"
                  label={t("farmDetails.landLeased")}
                  rules={[{ required: true, message: t("farmDetails.errorMessage.farmSizeMissing") }]}
                  initialValue={farm ? farm.size : undefined}
                  precision={2}
                  step={0.01}
                  min={0}
                  suffix="ha"
                />
              </Col>
            </Row>

            <Row className="margin-top-200">
              <Col className="padding-0">
                <AddressForm dataTestId="create-edit-farm" address={farm ?? null} countryCode={defaultCountry} />
              </Col>
            </Row>

            <Row className="margin-top-200">
              <Col>
                {/* @ts-expect-error need to convert to tsx */}
                <InputMasked
                  dataTestId="create-farm-phone-input"
                  name="phone"
                  label={t("farmDetails.phone")}
                  rules={[{ required: true, message: t("farmDetails.errorMessage.phoneMissing") }]}
                  mask="+00 00 000000[000]"
                  initialValue={farm?.phone}
                />
              </Col>
            </Row>
            <StickyFooterButtons absolute handleBack={handleBack}>
              <Form.Item>
                <Button dataTestId="create-farm-save-button" type="primary" htmlType="submit" disabled={saving} loading={saving}>
                  {selectedFarmId ? t("farmUpdateButton") : t("farmDetails.continue")}
                </Button>
              </Form.Item>
            </StickyFooterButtons>
          </Form>
        ) : (
          <PageSpin />
        )}
      </section>
    </>
  );
};
